import {
  ALL_LEADER_GROUPS_QUERY,
  type LeaderGroupNode,
} from '@/modules/companies'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ComboboxCommonProps, ComboboxItem } from './combobox'
import { Combobox } from './combobox'

interface CommonProps extends ComboboxCommonProps {
  alwaysInclude?: Omit<LeaderGroupNode, '__typename'> | null
  exclude?: string[]
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: string
  onChange(value: string): void
}

interface MultiProps extends CommonProps {
  mode: 'multiple'
  value: string[]
  onChange(value: string[]): void
}

type Props = SingleProps | MultiProps

export function LeaderGroupCombobox({
  mode,
  value,

  alwaysInclude = null,
  exclude = [],

  placeholder,
  searchPlaceholder,
  noResultsText,

  onChange,

  ...props
}: Props) {
  const translations = useTranslate({
    single: {
      placeholder: 'components.combobox.leader-group.placeholder',
      searchPlaceholder: 'components.combobox.leader-group.search-placeholder',
      noResults: 'components.combobox.leader-group.no-results',
    },
    multiple: {
      placeholder: 'components.multi-combobox.leader-group.placeholder',
      searchPlaceholder:
        'components.multi-combobox.leader-group.search-placeholder',
      noResults: 'components.multi-combobox.leader-group.no-results',
    },
  })

  const { data: leaderGroupsData } = useQuery(ALL_LEADER_GROUPS_QUERY)

  const leaderGroupItems: ComboboxItem[] = (() => {
    const leaderGroups = leaderGroupsData?.allLeaderGroups ?? []

    const items = leaderGroups
      .filter(
        leaderGroup =>
          leaderGroup.id !== alwaysInclude?.id &&
          !exclude.includes(leaderGroup.id)
      )
      .map<ComboboxItem>(user => ({
        value: user.id,
        label: user.name,
      }))

    if (!!alwaysInclude)
      items.unshift({
        value: alwaysInclude.id,
        label: alwaysInclude.name,
      })

    return items
  })()

  if (mode === 'single')
    return (
      <Combobox
        {...props}
        mode="single"
        value={value}
        items={leaderGroupItems}
        clearable
        placeholder={placeholder ?? translations.single.placeholder}
        searchPlaceholder={
          searchPlaceholder ?? translations.single.searchPlaceholder
        }
        noResultsText={noResultsText ?? translations.single.noResults}
        onChange={onChange}
      />
    )

  return (
    <Combobox
      {...props}
      mode="multiple"
      value={value}
      items={leaderGroupItems}
      placeholder={placeholder ?? translations.multiple.placeholder}
      searchPlaceholder={
        searchPlaceholder ?? translations.multiple.searchPlaceholder
      }
      noResultsText={noResultsText ?? translations.multiple.noResults}
      onChange={onChange}
    />
  )
}
